import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  Check, ChevronDown, ChevronRight, Plus, AlertTriangle,
  Target, Skull, Zap, Loader2, CheckSquare, Layers,
} from 'lucide-react';
import { CATEGORY_CONFIG } from './decisionConstants';
import DecisionRow from './DecisionRow';
import QuickAddBar from './QuickAddBar';
import BulkActionBar from './BulkActionBar';
import { ContextFilterBar, BatchFlowPanel } from './DecisionQueueParts';
import { useContextBatches } from '../../hooks/useContextBatches';

export default function DecisionQueue({
  decisions,
  focusTaskId,
  onToggleComplete, onSelect, onDefer, onSchedule, onFocus, onDelegate,
  onCallAction, onPaymentAction, onReadingAction, onArchive, onSweep,
  onRename,
  onBulkComplete, onBulkDefer, onBulkArchive,
  quickAdd, setQuickAdd, quickAddSchedule, setQuickAddSchedule,
  onQuickAdd, onNewTask, loading
}) {
  const [expandedCats, setExpandedCats] = useState({ DO_NOW: true, DELEGATE: true, DEFER: false, IGNORE: false, GRAVEYARD: false, COMPLETED: false });
  const [selectedIdx, setSelectedIdx] = useState(-1);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [contextFilter, setContextFilter] = useState('');
  const [batchFlow, setBatchFlow] = useState(null); // { items: [], current: 0 }
  const { batches: contextBatches } = useContextBatches();
  const contextTags = useMemo(() => [
    // `activeText` keeps label readable when the pill fills with `color`
    // (the "All" pill uses the text token as fill — inverted text needed in dark mode).
    { key: '', label: 'All', color: 'var(--pd-text)', activeText: 'var(--pd-bg)' },
    ...contextBatches,
  ], [contextBatches]);
  const containerRef = useRef(null);
  const handlersRef = useRef({});
  handlersRef.current = { onSelect, onDefer, onSchedule, onFocus, onToggleComplete, onCallAction, onPaymentAction, onReadingAction, onArchive, onRename };

  const toggle = (cat) => setExpandedCats(prev => ({ ...prev, [cat]: !prev[cat] }));

  const filteredDecisions = useMemo(() =>
    contextFilter ? decisions.filter(d => d.context === contextFilter) : decisions,
    [decisions, contextFilter]);

  const grouped = useMemo(() => ({
    DO_NOW: filteredDecisions.filter(d => d.category === 'DO_NOW'),
    DELEGATE: filteredDecisions.filter(d => d.category === 'DELEGATE'),
    DEFER: filteredDecisions.filter(d => d.category === 'DEFER'),
    IGNORE: filteredDecisions.filter(d => d.category === 'IGNORE'),
    GRAVEYARD: filteredDecisions.filter(d => d.category === 'GRAVEYARD'),
    COMPLETED: filteredDecisions.filter(d => d.category === 'COMPLETED'),
  }), [filteredDecisions]);

  const visibleDecisions = useMemo(() =>
    Object.entries(grouped)
      .filter(([cat]) => expandedCats[cat])
      .flatMap(([, items]) => items),
    [grouped, expandedCats]);

  const toggleSelectItem = useCallback((decision) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      const key = `${decision.type}-${decision.id}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setSelectMode(false);
  }, []);

  const selectedDecisions = useMemo(() =>
    decisions.filter(d => selectedIds.has(`${d.type}-${d.id}`)),
    [decisions, selectedIds]);

  const handleBulkComplete = useCallback(() => {
    if (onBulkComplete) onBulkComplete(selectedDecisions);
    clearSelection();
  }, [selectedDecisions, onBulkComplete, clearSelection]);

  const handleBulkDefer = useCallback(() => {
    if (onBulkDefer) onBulkDefer(selectedDecisions);
    clearSelection();
  }, [selectedDecisions, onBulkDefer, clearSelection]);

  const handleBulkArchive = useCallback(() => {
    if (onBulkArchive) onBulkArchive(selectedDecisions);
    clearSelection();
  }, [selectedDecisions, onBulkArchive, clearSelection]);

  const executeAction = useCallback((sel, key) => {
    if (!sel) return;
    const h = handlersRef.current;
    switch (key) {
      case 'Enter': h.onSelect?.(sel); break;
      case 'd': case 'D': h.onDefer?.(sel); break;
      case 's': case 'S': h.onSchedule?.(sel); break;
      case 'f': case 'F': if (sel.type === 'task') h.onFocus?.(sel); break;
      case 'x': case 'X':
        if (selectMode) { toggleSelectItem(sel); break; }
        if (sel.type === 'task') h.onToggleComplete?.(sel);
        else if (sel.type === 'call') h.onCallAction?.(sel, 'complete');
        else if (sel.type === 'payment') h.onPaymentAction?.(sel, 'paid');
        else if (sel.type === 'reading') h.onReadingAction?.(sel, 'read');
        break;
      case 'a': case 'A': if (sel.category === 'GRAVEYARD') h.onArchive?.(sel); break;
      default: break;
    }
  }, [selectMode, toggleSelectItem]);

  const handleKeyboard = useCallback((e) => {
    if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
    const sel = visibleDecisions[selectedIdx];
    switch (e.key) {
      case 'ArrowDown': case 'j':
        e.preventDefault();
        setSelectedIdx(i => Math.min(i + 1, visibleDecisions.length - 1));
        break;
      case 'ArrowUp': case 'k':
        e.preventDefault();
        setSelectedIdx(i => Math.max(i - 1, 0));
        break;
      case 'Escape':
        if (selectMode) { clearSelection(); break; }
        setSelectedIdx(-1);
        break;
      default:
        executeAction(sel, e.key);
        break;
    }
  }, [visibleDecisions, selectedIdx, executeAction, selectMode, clearSelection]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyboard);
    return () => window.removeEventListener('keydown', handleKeyboard);
  }, [handleKeyboard]);

  useEffect(() => {
    if (selectedIdx >= 0) {
      const el = containerRef.current?.querySelector(`[data-decision-idx="${selectedIdx}"]`);
      el?.scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIdx]);

  const deferralWarnings = decisions.filter(d => d.deferralCount >= 3 && d.category !== 'GRAVEYARD').length;
  const activeDecisions = decisions.filter(d => d.category !== 'COMPLETED');

  return (
    <div data-testid="decision-queue" className="flex-1 flex flex-col overflow-hidden"
      style={{ background: 'var(--pd-bg-soft)' }}>

      {/* Header */}
      <div className="px-5 pt-3 pb-2 flex items-center gap-2"
        style={{ background: 'var(--pd-panel)', borderBottom: '1px solid var(--pd-border)' }}>
        <div className="flex-1 min-w-0">
          <div className="text-[13px] font-semibold"
            style={{ color: 'var(--pd-text)', letterSpacing: '-0.02em' }}>Decision Queue</div>
          <div className="text-[10px] flex items-center gap-2" style={{ color: 'var(--pd-subtle)' }}>
            {decisions.length} active · {grouped.DO_NOW.length} need action now
            {deferralWarnings > 0 && (
              <span data-testid="deferral-warning-count" className="flex items-center gap-0.5" style={{ color: 'var(--pd-warning)' }}>
                <AlertTriangle className="w-3 h-3" />{deferralWarnings} avoiding
              </span>
            )}
          </div>
        </div>
        <span className="text-[10px] px-2 py-1 rounded-lg font-medium tabular-nums"
          style={{ background: 'var(--pd-panel-2)', color: 'var(--pd-muted)' }}>
          {(() => {
            const mins = decisions.reduce((s, d) => s + (d.estimatedMinutes || 0), 0);
            const h = Math.floor(mins / 60);
            return h > 0 ? `${h}h ${mins % 60}m total` : `${mins}m total`;
          })()}
        </span>
        {activeDecisions.length > 1 && (
          <button data-testid="bulk-select-toggle" onClick={() => { if (selectMode) clearSelection(); else setSelectMode(true); }}
            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-semibold transition-colors"
            style={{
              background: selectMode ? 'var(--pd-text)' : 'var(--pd-panel-2)',
              color: selectMode ? 'white' : 'var(--pd-muted)',
            }}>
            <CheckSquare className="w-3 h-3" />{selectMode ? 'Cancel' : 'Select'}
          </button>
        )}
        {decisions.length > 0 && onSweep && !selectMode && (
          <button data-testid="sweep-mode-trigger" onClick={onSweep}
            className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-semibold transition-colors"
            style={{ background: 'var(--pd-focus)', color: 'white' }}>
            <Zap className="w-3 h-3" />Sweep
          </button>
        )}
      </div>

      {/* Context batch bar */}
      <ContextFilterBar
        contextFilter={contextFilter}
        setContextFilter={(key) => { setContextFilter(key); setBatchFlow(null); }}
        contextTags={contextTags}
        decisions={decisions}
        filteredDecisions={filteredDecisions}
        onStartBatchFlow={() => {
          const items = filteredDecisions.filter(d => d.category !== 'COMPLETED');
          setBatchFlow({ items, current: 0 });
        }}
      />

      {/* Batch Focus Flow overlay */}
      <BatchFlowPanel
        batchFlow={batchFlow}
        onComplete={(item) => {
          onToggleComplete?.(item);
          setBatchFlow(prev => prev.current < prev.items.length - 1 ? { ...prev, current: prev.current + 1 } : null);
        }}
        onSkip={() => setBatchFlow(prev => prev.current < prev.items.length - 1 ? { ...prev, current: prev.current + 1 } : null)}
        onDefer={(item) => {
          onDefer?.(item);
          setBatchFlow(prev => prev.current < prev.items.length - 1 ? { ...prev, current: prev.current + 1 } : null);
        }}
        onExit={() => setBatchFlow(null)}
      />

      {/* Decision list */}
      <div ref={containerRef} className="flex-1 overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="px-5 py-4 space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: 'var(--pd-subtle)' }} />
              <span className="text-[11px] font-medium" style={{ color: 'var(--pd-subtle)' }}>Loading decisions...</span>
            </div>
            {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-[48px] rounded-xl skeleton-shimmer" style={{ animationDelay: `${i * 120}ms` }} />)}
          </div>
        ) : decisions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8" data-testid="decision-queue-empty">
            <div className="relative mb-4">
              <div
                aria-hidden="true"
                className="absolute inset-0 rounded-full blur-2xl opacity-60"
                style={{ background: 'radial-gradient(closest-side, var(--pd-brand, var(--pd-accent-base)), transparent 70%)' }}
              />
              <Target className="relative w-10 h-10" style={{ color: 'var(--pd-brand, var(--pd-accent-base))' }} />
            </div>
            <div className="text-[14px] font-semibold mb-1" style={{ color: 'var(--pd-text)' }}>Queue is clear</div>
            <p className="text-[12px] mb-5 max-w-[260px]" style={{ color: 'var(--pd-subtle)' }}>
              You're caught up. Capture the next thought before it gets away.
            </p>
            <div className="flex flex-col items-center gap-3">
              <button
                onClick={onNewTask}
                data-testid="decision-queue-empty-cta"
                className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white btn-primary"
              >
                <Plus className="w-3.5 h-3.5 inline mr-1" />Add task
              </button>
              <div className="flex items-center gap-2 text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
                <span>or press</span>
                <kbd className="kbd-pill text-[10px]">⌘K</kbd>
                <span>for the command bar</span>
              </div>
            </div>
          </div>
        ) : (
          <>
            {(() => {
              let globalIdx = 0;
              return Object.entries(grouped).map(([category, items]) => {
                if (items.length === 0) return null;
                const config = CATEGORY_CONFIG[category];
                const isExpanded = expandedCats[category];
                const startIdx = globalIdx;
                if (isExpanded) globalIdx += items.length;
                return (
                  <div key={category} data-testid={`decision-category-${category.toLowerCase()}`}>
                    <button onClick={() => toggle(category)}
                      className="w-full flex items-center gap-2 px-5 py-2"
                      style={{ borderBottom: '1px solid var(--pd-border-subtle)', background: isExpanded ? 'var(--pd-panel)' : 'transparent' }}>
                      {isExpanded
                        ? <ChevronDown className="w-3 h-3" style={{ color: config.color }} />
                        : <ChevronRight className="w-3 h-3" style={{ color: config.color }} />}
                      {category === 'GRAVEYARD' ? <Skull className="w-3.5 h-3.5" style={{ color: config.color }} /> : category === 'COMPLETED' ? <Check className="w-3.5 h-3.5" style={{ color: config.color }} /> : <div className="w-1.5 h-1.5 rounded-full" style={{ background: config.color }} />}
                      <span className="text-[10px] uppercase tracking-[0.08em] font-semibold" style={{ color: config.color }}>
                        {config.label}
                      </span>
                      <span className="text-[10px] tabular-nums" style={{ color: 'var(--pd-subtle)' }}>({items.length})</span>
                      <span className="text-[9px] ml-auto hidden sm:inline" style={{ color: 'var(--pd-subtle)' }}>{config.desc}</span>
                    </button>
                    {isExpanded && items.map((d, i) => (
                      <DecisionRow
                        key={`${d.type}-${d.id}`}
                        decision={d}
                        globalIdx={startIdx + i}
                        selectedIdx={selectedIdx}
                        selectMode={selectMode}
                        isChecked={selectedIds.has(`${d.type}-${d.id}`)}
                        isFocused={focusTaskId === d.id && d.type === 'task'}
                        onToggleCheck={toggleSelectItem}
                        onToggleComplete={onToggleComplete}
                        onSelect={onSelect}
                        onDefer={onDefer}
                        onSchedule={onSchedule}
                        onFocus={onFocus}
                        onDelegate={onDelegate}
                        onCallAction={onCallAction}
                        onPaymentAction={onPaymentAction}
                        onReadingAction={onReadingAction}
                        onArchive={onArchive}
                        onRename={onRename}
                      />
                    ))}
                  </div>
                );
              });
            })()}
          </>
        )}
      </div>

      {/* Bulk Action Bar or Quick Add */}
      {selectMode && selectedIds.size > 0 ? (
        <BulkActionBar
          selected={selectedDecisions}
          onBulkComplete={handleBulkComplete}
          onBulkDefer={handleBulkDefer}
          onBulkArchive={handleBulkArchive}
          onClearSelection={clearSelection}
        />
      ) : (
        <QuickAddBar
          quickAdd={quickAdd}
          setQuickAdd={setQuickAdd}
          quickAddSchedule={quickAddSchedule}
          setQuickAddSchedule={setQuickAddSchedule}
          onQuickAdd={onQuickAdd}
        />
      )}
    </div>
  );
}
